import { AuthError, type AuthClient } from '../../auth/auth-client';
import { trimLoginIdentifier } from '../../auth/identifier';
import type { SessionStore } from '../../auth/session-store';
import {
  AccountError,
  type AccountClient,
  type AccountProfile,
} from '../../account/account-client';
import type { ProfileShip } from '../../account/profile-ship';
import type {
  LoadoutBuild,
  LoadoutClient,
  LoadoutConfirmResult,
  LoadoutView,
} from '../../account/loadout-client';
import type { Connection } from '../../net/connection';
import { Outbound } from '../../net/outbound';
import { sanitizeJoinName } from './join';
import { socketState } from '../socket-state.svelte';
import { features } from '../../features/feature-state.svelte';
import { CUSTOM_LOBBIES } from '../../features/overrides';
import {
  DebugCommandType,
  LobbyCloseReason,
  NotificationSubject,
} from '../../protocol/gen/warinspace/v2/common_pb.js';
import type { LobbyState } from '../../protocol/gen/warinspace/v2/server_to_client_pb.js';
import { difficultyToWire, teamToWire, visibilityToWire } from '../../lobby/from-wire';
import type { JoinDeepLink, LobbyVisibility, PlaceholderDifficulty } from '../../lobby/lobby-state';

export type ScreenName =
  'pregame' | 'login' | 'register' | 'redeem' | 'shop' | 'loadout' | 'worlds' | 'lobbies';

export interface JoinFields {
  readonly name: string;
  readonly team: number;
}

export interface RegisterFields {
  readonly username: string;
  readonly email: string;
  readonly password: string;
}

export class ShellController {
  screen = $state<ScreenName>('pregame');
  status = $state('Connecting…');
  playDisabled = $state(true);

  loginError = $state('');
  registerError = $state('');
  redeemMessage = $state('');
  redeemError = $state('');
  shopMessage = $state('');
  shopError = $state('');
  busy = $state(false);

  profile = $state<AccountProfile | null>(null);
  profileIconMessage = $state('');
  profileIconError = $state('');

  loggedIn = $state(false);
  sessionUsername = $state('');
  nameSuggestion = $state<string | null>(null);

  private connectionRef: Connection | null = null;

  private awaitingDebugCredits = false;

  lobbyMessage = $state('');

  private lastJoin: JoinFields | null = null;
  private quitToMenuRequested = false;

  private respawnDeclined = false;
  private lobbyJoinCode: string | null = null;
  private pendingDeepLink: JoinDeepLink | null = null;
  private deepLinkSettled: (() => void) | null = null;
  private deepLinkOutstanding = false;
  private deepLinkSpectate = false;

  constructor(
    private readonly sessions: SessionStore,
    private readonly auth: AuthClient,
    private readonly account: AccountClient,
    private readonly loadouts: LoadoutClient,
  ) {
    this.refreshSession();
  }

  refreshSession(): void {
    const session = this.sessions.current;
    this.loggedIn = session !== null;
    this.sessionUsername = session?.username ?? '';
    if (session === null) this.profile = null;
    else void this.loadAccount();
  }

  attachConnection(connection: Connection): void {
    this.connectionRef = connection;
    features.onOverridesChanged(() => {
      this.sendFeatureOverrides();
    });
  }

  private sendFeatureOverrides(): void {
    this.connection.send(Outbound.setFeatureOverrides(features.overrides));
  }

  sendDebugCommand(type: DebugCommandType, amount: number): void {
    this.connection.send(Outbound.debugCommand(type, amount));
    if (type === DebugCommandType.GRANT_CREDITS) this.awaitingDebugCredits = true;
  }

  noteCreditBalance(credit: number): void {
    if (!this.awaitingDebugCredits || this.profile === null) return;
    this.awaitingDebugCredits = false;
    this.profile = { ...this.profile, credit };
  }

  get lastJoinName(): string | null {
    return this.lastJoin?.name ?? null;
  }

  private get connection(): Connection {
    if (this.connectionRef === null) {
      throw new Error('ShellController used before attachConnection().');
    }
    return this.connectionRef;
  }

  navigate(screen: ScreenName): void {
    this.loginError = '';
    this.registerError = '';
    this.redeemMessage = '';
    this.redeemError = '';
    this.profileIconMessage = '';
    this.profileIconError = '';
    this.shopMessage = '';
    this.shopError = '';
    this.screen = screen;
    if (screen === 'shop') void this.loadShop();
    if (screen === 'worlds') this.connection.send(Outbound.queryWorlds());
    if (screen === 'lobbies') this.refreshLobbies();
  }

  setStatus(text: string): void {
    this.status = text;
  }

  async buy(productId: number): Promise<void> {
    const session = this.sessions.current;
    this.shopMessage = '';
    this.shopError = '';
    if (session === null) {
      this.shopError = 'Log in to buy anything.';
      return;
    }

    try {
      const result = await this.account.purchase(session, productId);
      socketState.notePurchase(productId, result.credit);
      if (this.profile !== null) this.profile = { ...this.profile, credit: result.credit };
      this.shopMessage = 'Bought.';
      this.connection.send(Outbound.refreshAccount());
    } catch (error) {
      this.shopError = error instanceof AccountError ? error.message : 'Could not buy that.';
    }
  }

  async equip(productId: number): Promise<void> {
    const session = this.sessions.current;
    this.shopMessage = '';
    this.shopError = '';
    if (session === null) {
      this.shopError = 'Log in to equip anything.';
      return;
    }

    try {
      await this.account.equip(session, productId);
      this.connection.send(Outbound.refreshAccount());
    } catch (error) {
      this.shopError = error instanceof AccountError ? error.message : 'Could not equip that.';
    }
  }

  get profileShip(): ProfileShip | null {
    return this.profile?.profileShip ?? null;
  }

  async setProfileIcon(shipId: number): Promise<void> {
    const session = this.sessions.current;
    this.profileIconMessage = '';
    this.profileIconError = '';
    if (session === null) {
      this.profileIconError = 'Log in to set a profile icon.';
      return;
    }

    try {
      const captured = await this.account.setProfileShip(session, shipId);
      this.profile = this.profile === null ? null : { ...this.profile, profileShip: captured };
      this.profileIconMessage = 'Profile icon captured.';
    } catch (error) {
      this.profileIconError =
        error instanceof AccountError ? error.message : 'Could not set that as your profile icon.';
    }
  }

  private requireSession() {
    const session = this.sessions.current;
    if (session === null) {
      throw new AccountError('Log in to customize a ship.', 'session-invalid');
    }
    return session;
  }

  loadLoadout(shipId: number): Promise<LoadoutView> {
    return this.loadouts.get(this.requireSession(), shipId);
  }

  async confirmLoadout(
    shipId: number,
    build: LoadoutBuild,
    expectedPrice: number | null,
  ): Promise<LoadoutConfirmResult> {
    const result = await this.loadouts.confirm(this.requireSession(), shipId, build, expectedPrice);
    if (this.profile !== null) this.profile = { ...this.profile, credit: result.credit };
    this.connection.send(Outbound.refreshAccount());
    return result;
  }

  refreshLobbies(): void {
    if (!features.enabled(CUSTOM_LOBBIES)) return;
    this.connection.send(Outbound.queryLobbies());
  }

  createLobby(visibility: LobbyVisibility, name: string): void {
    if (!features.enabled(CUSTOM_LOBBIES)) return;
    if (!this.loggedIn) {
      this.lobbyMessage = 'Log in to create a lobby.';
      return;
    }
    this.lobbyMessage = '';
    this.connection.send(Outbound.createLobby(visibilityToWire(visibility), name));
  }

  joinLobby(joinCode: string, spectate = false): void {
    if (!features.enabled(CUSTOM_LOBBIES)) return;
    this.lobbyMessage = '';
    this.connection.send(Outbound.joinLobby(joinCode, spectate));
  }

  leaveLobby(): void {
    this.connection.send(Outbound.leaveLobby());
  }

  cancelLobbyStart(): void {
    this.connection.send(Outbound.cancelLobbyStart());
  }

  startLobby(): void {
    this.connection.send(Outbound.startLobby());
  }

  takeLobbySeat(slotId: number): void {
    this.connection.send(Outbound.takeLobbySeat(slotId));
  }

  sendChat(text: string): void {
    this.connection.send(Outbound.sendChat(text));
  }

  spectateLobby(): void {
    this.connection.send(Outbound.spectateLobby());
  }

  setTeamDifficulty(team: number, difficulty: PlaceholderDifficulty): void {
    this.connection.send(
      Outbound.setTeamDifficulty(teamToWire(team), difficultyToWire(difficulty)),
    );
  }

  queueJoinCode(link: JoinDeepLink, settled: () => void): void {
    this.pendingDeepLink = link;
    this.deepLinkSettled = settled;
  }

  /** A `JoinLobby` is only routed once the connection holds a room, and `WorldSetup` is the first thing a room sends it. */
  roomReady(): void {
    if (Object.keys(features.overrides).length > 0) this.sendFeatureOverrides();

    const link = this.pendingDeepLink;
    if (link === null) return;
    this.pendingDeepLink = null;
    if (!link.complete) {
      this.reportLobbyFailure('That join link is not a whole code. Check it and try again.');
      return;
    }
    this.deepLinkOutstanding = true;
    this.deepLinkSpectate = link.spectate;
    this.joinLobby(link.code, link.spectate);
  }

  /** The code is left in the address bar, because a refused join is the one worth reloading. */
  noteNotification(text: string, subject: NotificationSubject): void {
    if (!this.deepLinkOutstanding) return;
    if (subject !== NotificationSubject.LOBBY_JOIN) return;
    this.deepLinkOutstanding = false;
    this.reportLobbyFailure(text);
  }

  noteLobbyState(state: LobbyState): string | null {
    const wanted = this.deepLinkOutstanding;
    const spectating = this.deepLinkSpectate;
    this.deepLinkOutstanding = false;
    this.settleDeepLink();
    if (this.lobbyJoinCode !== state.joinCode) {
      this.lobbyJoinCode = state.joinCode;
      this.screen = 'pregame';
      this.lobbyMessage = '';
      this.join({ name: this.lobbyName(), team: 2 });
    }
    return wanted && state.started && !spectating
      ? 'That match had already started. You are watching it.'
      : null;
  }

  private settleDeepLink(): void {
    const settled = this.deepLinkSettled;
    this.deepLinkSettled = null;
    settled?.();
  }

  private reportLobbyFailure(text: string): void {
    this.setVisible(true);
    this.navigate('lobbies');
    this.lobbyMessage = text;
  }

  lobbyClosed(reason: LobbyCloseReason): void {
    this.lobbyJoinCode = null;
    this.quitToMenuRequested = false;
    this.lobbyMessage = lobbyClosedMessage(reason);
    this.setVisible(true);
    this.setStatus(this.lobbyMessage);
  }

  private lobbyName(): string {
    return sanitizeJoinName(
      this.lastJoin?.name ?? this.sessions.rememberedJoinName() ?? this.sessionUsername,
    );
  }

  chooseWorld(serverNum: number): void {
    this.connection.send(Outbound.changeWorld(serverNum));
    this.navigate('pregame');
  }

  get visible(): boolean {
    return socketState.shellVisible;
  }

  setVisible(visible: boolean): void {
    const returning = visible && !this.visible;
    socketState.shellVisible = visible;
    if (returning && this.screen !== 'pregame') {
      this.screen = 'pregame';
    }
  }

  setPlayable(playable: boolean): void {
    this.playDisabled = !playable;
  }

  join(fields: JoinFields): void {
    const name = sanitizeJoinName(fields.name);
    this.lastJoin = { name, team: fields.team };
    this.sessions.rememberJoinName(name);
    this.quitToMenuRequested = false;
    this.connection.send(Outbound.joinGame(fields.team, name));
    this.setStatus('Joining…');
  }

  requestReturnToMenuOnDeath(): void {
    this.quitToMenuRequested = true;
  }

  returnToMenu(): void {
    this.quitToMenuRequested = false;
    this.setVisible(true);
  }

  noteDeadState(dead: boolean): void {
    if (!dead) {
      this.respawnDeclined = false;
      return;
    }
    if (!this.quitToMenuRequested || this.respawnDeclined) return;
    this.respawnDeclined = true;
    this.quitToMenuRequested = false;
    this.connection.send(Outbound.notPlayingNextRound());
    this.setVisible(true);
    this.setStatus('You died. Play again?');
  }

  respawnAfterDeath(): void {
    if (this.respawnDeclined || this.lastJoin === null) return;
    this.setStatus('Respawning…');
  }

  async login(username: string, password: string): Promise<void> {
    const trimmedUsername = trimLoginIdentifier(username);
    if (trimmedUsername === '' || password === '') return;

    this.loginError = '';
    this.busy = true;
    try {
      const session = await this.auth.login({ username: trimmedUsername, password });
      this.sessions.set(session);
      this.refreshSession();
      this.nameSuggestion = session.username;
      this.setStatus(`Logged in as ${session.username}`);
      this.connection.reconnect();
      this.navigate('pregame');
    } catch (error) {
      this.loginError = error instanceof AuthError ? error.message : 'Could not log in. Try again.';
    } finally {
      this.busy = false;
    }
  }

  async register(fields: RegisterFields): Promise<void> {
    const username = fields.username.trim();
    const email = fields.email.trim();
    if (username === '' || email === '' || fields.password === '') return;

    this.registerError = '';
    this.busy = true;
    try {
      const session = await this.auth.register({ username, email, password: fields.password });
      this.sessions.set(session);
      this.refreshSession();
      this.setStatus(`Registered as ${session.username}`);
      this.connection.reconnect();
      this.navigate('pregame');
    } catch (error) {
      this.registerError =
        error instanceof AuthError ? error.message : 'Could not register. Try again.';
    } finally {
      this.busy = false;
    }
  }

  async redeem(code: string): Promise<void> {
    const trimmed = code.trim();
    const session = this.sessions.current;
    if (trimmed === '' || session === null) return;

    this.redeemError = '';
    this.redeemMessage = '';
    this.busy = true;
    try {
      const result = await this.account.redeem(session, trimmed);
      this.profile = this.profile === null ? null : { ...this.profile, credit: result.credit };
      this.redeemMessage =
        result.creditsGranted > 0
          ? `Redeemed: +${String(result.creditsGranted)} credits.`
          : 'Code redeemed.';
    } catch (error) {
      this.redeemError =
        error instanceof AccountError ? error.message : 'Could not redeem that code.';
    } finally {
      this.busy = false;
    }
  }

  async logout(): Promise<void> {
    const session = this.sessions.current;
    this.sessions.clear();
    this.refreshSession();
    this.setStatus('Connected as a guest');
    this.connection.reconnect();
    this.navigate('pregame');
    if (session !== null) await this.auth.logout(session);
  }

  /** Seeds the wallet/wardrobe over HTTP: a socket authenticated on its handshake is not sent an `AccountSnapshot` until the first purchase, so without this the shop shows nothing owned until then. Silent on failure — the socket is still the authority. */
  private async loadShop(): Promise<void> {
    const session = this.sessions.current;
    if (session === null) return;

    try {
      const [profile, owned, equipped] = await Promise.all([
        this.account.profile(session),
        this.account.purchases(session),
        this.account.cosmetics(session),
      ]);
      this.profile = profile;
      socketState.seed({ credits: profile.credit, owned, equipped });
    } catch {
      // silent — see above
    }
  }

  private async loadAccount(): Promise<void> {
    const session = this.sessions.current;
    if (session === null) return;

    try {
      this.profile = await this.account.profile(session);
    } catch {
      this.profile = null;
    }
  }
}

function lobbyClosedMessage(reason: LobbyCloseReason): string {
  switch (reason) {
    case LobbyCloseReason.LEFT:
      return 'You left the lobby.';
    case LobbyCloseReason.MATCH_ENDED:
      return 'The match is over. The lobby has closed.';
    case LobbyCloseReason.RETIRED:
      return 'The host closed the lobby.';
    case LobbyCloseReason.UNSPECIFIED:
      return 'You are no longer in a lobby.';
  }
}
